import { Link, Outlet, useNavigate } from 'react-router';
import { gql, useMutation } from 'urql';
import { toast } from 'sonner';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { getInitials } from '@/utils';
import { useUser } from '@/components/auth-provider';
import { PROMO_DEFAULT_QUERIES, SUPPORT_EMAIL } from '@/utils/constants';

export const logoutQuery = gql`
  mutation Logout {
    logout
  }
`;

export default function NavBar() {
  const { clearUser, user } = useUser();
  const navigate = useNavigate();
  const [, logout] = useMutation(logoutQuery);

  async function handleLogout() {
    try {
      const result = await logout();

      if (result.data?.logout) {
        clearUser();
      }
    } catch (error) {
      toast.error('Logout failed');
    }
  }

  return (
    <>
      <header className="bg-background flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <img
            alt="Grocery TV"
            className="h-10 w-auto"
            src="/gtv-logo-blue.svg"
          />
          <Separator
            orientation="vertical"
            className="h-4 data-[orientation=vertical]:h-6"
          />
          <p className="text-muted-foreground ml-2 text-sm font-semibold">
            {user?.company.name ?? 'Retailer'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/promotions${PROMO_DEFAULT_QUERIES}`}>
            <Button className="hover:cursor-pointer" variant="link">
              Promotions
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-full" size="icon" variant="ghost">
                <Avatar className="size-8 rounded-full">
                  <AvatarFallback className="bg-primary dark:bg-primary-800 rounded-full text-sm text-white">
                    {getInitials(user?.name ?? user?.email)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => navigate('/account')}>
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/attributes')}>
                  Store Attributes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  Log out
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  asChild
                  className="flex flex-col items-start gap-0"
                >
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=Promo%20CMS`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <span className="text-muted-foreground text-xs">
                      Need help?
                    </span>
                    support@grocerytv.com
                  </a>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <div className="mt-4">
        <Outlet />
      </div>
    </>
  );
}
